import React from 'react';
import MediaQuery from 'react-responsive';
import PropTypes from 'prop-types';
import fetch from 'isomorphic-fetch';
import uniq from 'lodash/uniq';
import {
  Badge,
  CaptionText,
  HeadingText,
  Link,
  Container,
  Row,
  Col,
  v2blue600,
  v2grey600,
  BodyText,
  Pagination,
  white,
  DropdownMenu,
} from '@paypalcorp/pp-react';
import { ThemeProvider } from '@paypalcorp/pp-react-theme';
import glamorous from 'glamorous';

import { createHandler } from 'griffin-paypal/number';
import { DataContext } from '../lib/context';

import * as analytics from '../lib/analytics';
import { css } from 'glamor';

const SearchResultCharityTitle = glamorous(HeadingText)({
  left: '150px',
  right: '250px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '600px',
  '@media(max-width: 950px)': {
    maxWidth: '450px',
  },
  '@media(max-width: 768px)': {
    left: '70px',
    right: '0px',
    maxWidth: '200px',
  },
});

require('es6-promise').polyfill();

const spinerStyle = {
  textAlign: 'center',
  padding: '15px 0px 10px 0px',
  width: '200px',
  height: '60px',
  margin: 'auto',
};

const paginationStyle = css({
  transform: 'translateY(50%)',
  '@media(max-width: 768px)': {
    marginLeft: '4px',
    transform: 'translateY(25%)',
  },
});

const pageStyle = css({
  color: v2blue600,
  margin: '0 11px',
  ':hover': {
    color: v2blue600,
  },
  '@media(max-width: 500px)': {
    color: v2blue600,
    margin: '0 7px',
    ':hover': {
      color: v2blue600,
    },
  },
});

const activePageStyle = css({
  backgroundColor: v2blue600 + '!important',
  transition: 'none',
  ':hover': {
    color: white,
  },
});

const pageArrowSpaceStyle = css({
  width: '0',
});

const pageArrow = css({
  ':hover': {
    color: v2blue600,
  },
  '@media(max-width: 500px)': {
    margin: '0 0',
    padding: '4px 0px',
  },
});

const totalResultsTextStyle = {
  color: v2grey600,
};

export default class extends React.Component {
  static contextType = DataContext;
  static propTypes = {
    query: PropTypes.object,
    charities: PropTypes.array,
    maxCharityResults: PropTypes.number,
    totalItems: PropTypes.number,
    states: PropTypes.object,
    searchUrl: PropTypes.string,
    charityUrl: PropTypes.string,
    contentCountry: PropTypes.string,
    isMobile: PropTypes.bool,
  };

  state = {
    maxResults: this.props.maxCharityResults,
    page:
      this.props.query && this.props.query.page
        ? Number(this.props.query.page)
        : 1,
    isInfiniteLoading: false,
    charities: this.props.charities || [],
    totalPages: Number(this.props.totalPages),
    sortBy:
      this.props.query && this.props.query.sortBy
        ? this.props.query.sortBy
        : 'bestmatch',
    pageSize:
      this.props.query && this.props.query.pageSize
        ? Number(this.props.query.pageSize)
        : 24,
  };

  analyticsData = {
    e: 'cl',
    product: 'dw_giving',
    account_type: analytics.getAccountType(this.props.authState),
    event_source: analytics.getEventSource(this.props.isMobile),
    acct_cntry: this.props.contentCountry,
    filter_type:
      this.props.query && this.props.query.type ? this.props.query.type : '',
    state:
      this.props.query && this.props.query.state ? this.props.query.state : '',
    category_code:
      this.props.query && this.props.query.category
        ? this.props.query.category
        : '',
  };

  recordShowMoreEvent = () => {
    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_MORE_CHARITIES_PRESSED,
      this.analyticsData
    );
  };

  recordSortPressedEvent = () => {
    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_SORT_DROPDOWN_PRESSED,
      this.analyticsData
    );
  };

  recordSortSelectionEvent = (optSel) => {
    let optionSelected = 'best_match';
    if (optSel === 'name') {
      optionSelected = 'alphabetical';
    }

    if (optSel === 'trending') {
      optionSelected = 'popular';
    }

    if (optSel === 'bestmatch') {
      optionSelected = 'best_match';
    }

    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_SORT_SELECTION_PRESSED,
      Object.assign({}, this.analyticsData, {
        opt_sel: optionSelected,
      })
    );
  };

  recordPageSizePressedEvent = () => {
    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_PAGE_SIZE_DROPDOWN_PRESSED,
      this.analyticsData
    );
  };

  recordPageSizeSelectionEvent = (optSel) => {
    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_PAGE_SIZE_SELECTION_PRESSED,
      Object.assign({}, this.analyticsData, {
        opt_sel: optSel,
      })
    );
  };

  recordPaginationPressedEvent = (optSel) => {
    this.handleLoad(optSel);
    analytics.instance.recordEvent(
      analytics.events.DW_GIVING_BROWSE_FUNDRAISER_CHARITY_PAGINATION_PRESSED,
      Object.assign({}, this.analyticsData, {
        opt_sel: optSel,
      })
    );
  };

  handleLoad = (nextPage) => {
    const urlParams = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );

    const paramsObj = Object.assign({}, urlParams, {
      page: nextPage,
      pageSize: this.state.pageSize,
      sortBy: this.state.sortBy,
    });

    const params = new URLSearchParams(paramsObj);

    const url = new URL(window.location.origin);
    url.pathname = window.location.pathname;
    url.search = params;
    window.location = url;
  };

  elementInfiniteLoad = () => {
    return <div style={spinerStyle} className={'hasSpinner'} />;
  };

  handleAnalyticsCharityLink = (npid) => {
    analytics.trackLinkWithData(
      'charityLink',
      this.props.fptiInfo.pgrp,
      this.props.fptiInfo.page,
      { crid: npid },
      'link'
    );
  };

  handleAnalyticsStateLink = (npid) => {
    analytics.trackLinkWithData(
      'stateLink',
      this.props.fptiInfo.pgrp,
      this.props.fptiInfo.page,
      { crid: npid },
      'link'
    );
  };

  handleAnalyticsCharityDonate = (npid) => {
    analytics.trackLinkWithData(
      'DonateButton',
      this.props.fptiInfo.pgrp,
      this.props.fptiInfo.page,
      { crid: npid },
      'link'
    );
  };

  categoryList = (npid, categories) =>
    categories.map((category, categoryIndex) => {
      return (
        <a
          style={{ marginRight: '8px' }}
          key={`${npid}-${categoryIndex}`}
          href={`${this.props.resources.searchLink}?category=${category.value}`}
        >
          <ThemeProvider theme='v2'>
            <Badge className='category-badge' type='feature'>
              {category.label}
            </Badge>
          </ThemeProvider>
        </a>
      );
    });

  handlePageSize = (e) => {
    this.recordPageSizeSelectionEvent(e.target.value);
    this.setState(
      {
        pageSize: e.target.value,
      },
      () => {
        this.handleLoad(1);
      }
    );
  };

  handleSortBy = (e) => {
    this.recordSortSelectionEvent(e.target.value);
    this.setState(
      {
        sortBy: e.target.value,
      },
      () => {
        this.handleLoad(1);
      }
    );
  };

  renderItemRow = ({
    charity,
    categories,
    npid,
    index,
    logoUrl,
    cityState,
  } = {}) => {
    return (
      <Row key={`item-${npid}-${index}`} className='item'>
        <Col xs={4} md={3} lg={2}>
          <div
            className={'avatar'}
            onClick={() =>
              window.open(`${this.props.charityUrl}/${npid}`, '_self')
            }
            style={{
              background: `url(${logoUrl}) no-repeat center, center`,
              backgroundSize: 'contain',
            }}
          />
        </Col>
        <Col xs={8} md={7} lg={8}>
          <Container>
            <Row>
              <Link
                style={{ paddingRight: 0, paddingLeft: 0 }}
                onClick={this.handleAnalyticsCharityLink.bind(this, npid)}
                href={`${this.props.charityUrl}/${npid}`}
                secondary
                size='xl3'
              >
                <MediaQuery minWidth={769}>
                  <SearchResultCharityTitle as='h3' size='md' regular={true}>
                    {charity.name}
                  </SearchResultCharityTitle>
                </MediaQuery>
                <MediaQuery maxWidth={768}>
                  <SearchResultCharityTitle as='h3' size='sm' regular={true}>
                    {charity.name}
                  </SearchResultCharityTitle>
                </MediaQuery>
              </Link>
            </Row>
            <Row className='category-row'>
              {this.categoryList(npid, categories)}
            </Row>
            <Row>{cityState}</Row>
          </Container>
        </Col>
        <MediaQuery minWidth={769}>
          <Col
            style={{
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
            }}
          >
            <Link
              theme='v2'
              id={`nemo_searchResultsDonate-${index}`}
              onClick={this.handleAnalyticsCharityDonate.bind(this, npid)}
              href={`${this.props.charityUrl}/${npid}`}
            >
              {this.props.i18n('label.donate-button')}
            </Link>
          </Col>
        </MediaQuery>
      </Row>
    );
  };

  renderList = (charities) => {
    const sizePageOptions = [
      { primaryText: '24', value: 24 },
      { primaryText: '48', value: 48 },
      { primaryText: '94', value: 94 },
    ];

    const sortByOptions = [
      {
        primaryText: this.props.i18n('label.sort-by-best-match'),
        value: 'bestmatch',
      },
      {
        primaryText: this.props.i18n('label.sort-by-alphabetical'),
        value: 'name',
      },
      {
        primaryText: this.props.i18n('label.sort-by-popular'),
        value: 'trending',
      },
    ];

    const items = charities
      .filter((charity) => !!charity)
      .map((charity, index) => {
        const npid = charity.nonprofit_id;
        // default logo for fallback case
        const logoUrl =
          charity.logo_url ||
          `https://${this.props.cdnHost}/images/Cause/ic_donate.svg`;
        const address = charity.address ? charity.address : {};

        let categories;
        if (Array.isArray(charity.cause_area)) {
          const causeAreaIDs = uniq(charity.cause_area.map((ca) => ca.id));
          categories = causeAreaIDs.map((id) => ({
            label: this.props.i18n(`label.category-${id}`),
            value: String(id),
          }));
        } else {
          categories = [];
        }

        const cityName = [address.city, address.state]
          .filter((x) => x && x.trim())
          .join(', ');

        const cityState = (
          <ThemeProvider theme='v2'>
            <MediaQuery minWidth={769}>
              <BodyText style={{ paddingTop: '18px' }}>{cityName}</BodyText>
            </MediaQuery>
            <MediaQuery maxWidth={769}>
              <CaptionText style={{ paddingTop: '14px' }}>
                {cityName}
              </CaptionText>
            </MediaQuery>
          </ThemeProvider>
        );

        return (
          <>
            <MediaQuery minWidth={769}>
              {this.renderItemRow({
                charity,
                categories,
                npid,
                index,
                logoUrl,
                cityState,
              })}
            </MediaQuery>
            <MediaQuery maxWidth={769}>
              <Link
                style={{ padding: '0' }}
                className='tapable-field'
                onClick={this.handleAnalyticsCharityDonate.bind(this, npid)}
                href={`${this.props.charityUrl}/${npid}`}
              >
                {this.renderItemRow({
                  charity,
                  categories,
                  npid,
                  index,
                  logoUrl,
                  cityState,
                })}
              </Link>
            </MediaQuery>
          </>
        );
      });

    // Could be `undefined` for campaign search pages
    let totalResults = this.props.totalItems;
    if (this.props.totalItems > this.state.maxResults) {
      totalResults = this.state.maxResults;
    }

    const totalResultsText =
      charities.length === 1
        ? this.props.i18n('label.one-result')
        : totalResults &&
          this.props
            .i18n('label.total-charity-results')
            .replace(
              '{totalResults}',
              this.griffinNumberHandler.formatNumber(totalResults)
            );

    const totalResultsContainer = (
      <div className={'total-results-container'}>
        <BodyText style={totalResultsTextStyle}>{totalResultsText}</BodyText>
      </div>
    );

    let sortBy;
    if (this.props.i18n('label.sort-by').length > 1) {
      sortBy = (
        <ThemeProvider theme='v2'>
          <div className={'sort-by-container'}>
            {totalResultsContainer}
            {this.props.searchType === 'charities' && (
              <div className={'select-sort-by'}>
                <DropdownMenu
                  label={this.props.i18n('label.sort-by')}
                  options={sortByOptions}
                  value={this.state.sortBy}
                  onClick={this.recordSortPressedEvent}
                  onChange={this.handleSortBy}
                />
              </div>
            )}
          </div>
        </ThemeProvider>
      );
    }

    const selectPageSize = (
      <ThemeProvider theme='v2'>
        <div className={'select-page-size'}>
          <DropdownMenu
            label={this.props.i18n('label.show')}
            options={sizePageOptions}
            value={this.state.pageSize}
            onClick={this.recordPageSizePressedEvent}
            onChange={this.handlePageSize}
          />
        </div>
      </ThemeProvider>
    );

    let pagination;
    const totalPageCount =
      this.props.totalItems > this.state.maxResults
        ? Math.trunc(this.state.maxResults / this.state.pageSize)
        : Number(this.state.totalPages);
    if (this.state.totalPages > 1) {
      pagination = (
        <>
          <MediaQuery minWidth={769}>
            <div className='pagination-container'>
              <Pagination
                totalPageCount={totalPageCount}
                currentPage={this.state.page}
                onChange={this.recordPaginationPressedEvent}
                className={`${paginationStyle}`}
                activePageClassName={`${activePageStyle}`}
                pageClassName={`${pageStyle}`}
                arrowSpaceClassName={`${pageArrowSpaceStyle}`}
                arrowLeftClassName={`${pageArrow}`}
                arrowRightClassName={`${pageArrow}`}
              />
              {selectPageSize}
            </div>
          </MediaQuery>
          <MediaQuery maxWidth={768}>
            <div className='pagination-container'>
              <Pagination
                totalPageCount={totalPageCount}
                currentPage={this.state.page}
                onChange={this.recordPaginationPressedEvent}
                className={`${paginationStyle}`}
                activePageClassName={`${activePageStyle}`}
                pageClassName={`${pageStyle}`}
                arrowSpaceClassName={`${pageArrowSpaceStyle}`}
                arrowLeftClassName={`${pageArrow}`}
                arrowRightClassName={`${pageArrow}`}
              />
            </div>
          </MediaQuery>
        </>
      );
    }

    const urlParams = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );

    const paramsObj = Object.assign({}, urlParams, {
      type: 'charities',
      page: 1,
      pageSize: 24,
      sortBy: 'bestmatch',
    });

    const params = new URLSearchParams(paramsObj);

    const url = new URL(window.location.origin);
    url.pathname = window.location.pathname;
    url.search = params;

    const showMore = (
      <Link target={''} href={url} onClick={this.recordShowMoreEvent}>
        {this.props.i18n('label.show-more-charities')}
      </Link>
    );

    return (
      <>
        {sortBy}
        {this.props.searchType === 'charities' ? items : items.slice(0, 5)}
        {this.props.searchType === 'charities' && pagination}
        {items.length > 5 && this.props.searchType === 'all' && showMore}
      </>
    );
  };

  render() {
    if (!this.griffinNumberHandler) {
      this.griffinNumberHandler = createHandler(this.context.griffin.number);
    }

    return (
      <div className={'list'}>{this.renderList(this.state.charities)}</div>
    );
  }
}
